import { api } from '@core/api/client'
import { isAttributeEditableOnClass } from './lib/stepPatch'
import { getPredefinedTypeInfo } from './lib/predefinedType'
import type { IfcEnumSchema } from './lib/ifcEnums.generated'

// Allowed action names: must match `ALLOWED_ACTIONS` in
// `backend/modules/ifc_edit/router.py`.
export type IfcEditAuditAction =
  | 'ifc_edit.attribute.edit'
  | 'ifc_edit.attribute.clear'
  | 'ifc_edit.pset.write'
  | 'ifc_edit.pset.delete'
  | 'ifc_edit.export'

// Editable attribute names exposed by the inline editor in the
// PropertiesPanel. Each lives at a stable STEP-arg index across IFC
// 2x3, IFC 4 and IFC 4x3 within its inherited bucket OR, for
// PredefinedType: at a class- and schema-specific position
// described by the table in lib/predefinedType.ts:
//
//   Name, Description   → every IfcRoot (positions 2, 3)
//   ObjectType          → every IfcObject (position 4)
//   Tag                 → every IfcElement (position 7)
//   LongName            → every IfcSpatialElement /
//                         IfcSpatialStructureElement (position 7)
//   PredefinedType      → class- and schema-specific (enum slot)
//
// The STEP-level patcher resolves the position via inheritance (for
// string attributes) or via the PredefinedType meta table, and also
// verifies that the existing argument has the right shape before
// rewriting: a wrong guess can never corrupt the file.
export const EDITABLE_ATTRIBUTES = [
  'Name',
  'Description',
  'ObjectType',
  'Tag',
  'LongName',
  'PredefinedType',
] as const
export type EditableAttribute = (typeof EDITABLE_ATTRIBUTES)[number]

const EDITABLE_SET = new Set<string>(EDITABLE_ATTRIBUTES)

export function isEditableAttribute(name: string): name is EditableAttribute {
  return EDITABLE_SET.has(name)
}

/** Returns true when `attribute` is editable on entities of class
 *  `category` (the IFC class name, in either case) for the supplied
 *  schema. Schema is consulted for PredefinedType (which is enum-
 *  shaped and varies per schema); string slots ignore it.
 *
 *  Single source of truth for editability, used both by the
 *  PropertiesPanel and by the export pipeline. */
export function isAttributeEditableForCategory(
  attribute: string,
  category: string,
  schema: IfcEnumSchema | null = null,
): boolean {
  if (!isEditableAttribute(attribute)) return false
  return isAttributeEditableOnClass(category, attribute, schema)
}

/** Returns the legal enum values for an attribute on (category,
 *  schema), or null when the attribute is free-text. Currently the
 *  only enum-shaped editable attribute is PredefinedType, but the
 *  function is generic so we can add more later (OperationType,
 *  CompositionType, ...) without touching the panel. */
export function getEditableAttributeChoices(
  attribute: string,
  category: string,
  schema: IfcEnumSchema | null,
): readonly string[] | null {
  if (attribute !== 'PredefinedType') return null
  const info = getPredefinedTypeInfo(category, schema)
  return info?.choices ?? null
}

export const ifcEditApi = {
  /** Fire-and-forget audit ping. Edits run entirely client-side; the
   *  server only records that something happened (action + small
   *  metadata). Failures are silent so they never break the user's
   *  inline-edit flow. */
  audit(action: IfcEditAuditAction, metadata: Record<string, unknown> = {}): void {
    void api
      .post<void>('/api/modules/ifc-edit/audit', { action, metadata })
      .catch(() => {
        /* silent */
      })
  },
}
